import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Container, Row, Col, Form, Button, Modal } from "react-bootstrap";
import InputMask from "react-input-mask";
import { getPropertyList, addOffer } from "../actions/backOfficeAction";
import { toast } from "react-toastify";

const AddOfferForm = ({ idle }) => {
  const dispatch = useDispatch();
  const propertyList = useSelector((state) => state.backOfficeState.propertyList);

  // form state
  const [propertyPrefix, setPropertyPrefix] = useState("");
  const [swipeDate, setSwipeDate] = useState("");
  const [swipeTime, setSwipeTime] = useState("");
  const [swipeAmPm, setSwipeAmPm] = useState("AM");
  const [ticketId, setTicketId] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [playerId, setPlayerId] = useState("");
  const [dob, setDob] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [brand, setBrand] = useState("");
  const [amount, setAmount] = useState("");
  const [expirationDate, setExpirationDate] = useState("");
  const [expirationTime, setExpirationTime] = useState("");
  // Default the expiration time to PM since expiration is usually at night
  const [expirationAmPm, setExpirationAmPm] = useState("PM");

  // confirmation modal + errors
  const [showConfirm, setShowConfirm] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!idle) dispatch(getPropertyList());
  }, [dispatch, idle]);

  const resetForm = () => {
    setPropertyPrefix("");
    setSwipeDate("");
    setSwipeTime("");
    setSwipeAmPm("AM");
    setTicketId("");
    setFirstName("");
    setLastName("");
    setPlayerId("");
    setDob("");
    setZipCode("");
    setBrand("");
    setAmount("");
    setExpirationDate("");
    setExpirationTime("");
    setExpirationAmPm("PM");
    setErrorMessage("");
  };

  // Helper to build/format a date+time string as MM/dd/yyyy hh:mm:ss a
  const buildDateTime = (dateStr, timeStr, ampm) => {
    if (!dateStr || !timeStr) return null;
    const dateParts = dateStr.split("/");
    const timeParts = timeStr.split(":");
    if (dateParts.length !== 3 || timeParts.length !== 2) {
      return `${dateStr} ${timeStr} ${ampm}`;
    }
    const [mm, dd, yyyy] = dateParts.map((p) => parseInt(p, 10));
    let hour = parseInt(timeParts[0], 10);
    const minute = parseInt(timeParts[1], 10);
    if (Number.isNaN(mm) || Number.isNaN(dd) || Number.isNaN(yyyy) || Number.isNaN(hour) || Number.isNaN(minute)) {
      return `${dateStr} ${timeStr} ${ampm}`;
    }
    if (ampm && ampm.toUpperCase() === "PM") {
      if (hour < 12) hour += 12;
    } else {
      if (hour === 12) hour = 0;
    }
    const dt = new Date(yyyy, mm - 1, dd, hour, minute, 0);
    const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
    const outMM = pad(dt.getMonth() + 1);
    const outDD = pad(dt.getDate());
    const outYYYY = dt.getFullYear();
    let outHour = dt.getHours();
    const outMinute = pad(dt.getMinutes());
    const outSecond = pad(dt.getSeconds());
    const outAmPm = outHour >= 12 ? "PM" : "AM";
    outHour = outHour % 12;
    if (outHour === 0) outHour = 12;
    const outHourStr = pad(outHour);
    return `${outMM}/${outDD}/${outYYYY} ${outHourStr}:${outMinute}:${outSecond} ${outAmPm}`;
  };

  const formatSwipeDisplay = (dateStr, timeStr, ampm) => {
    const v = buildDateTime(dateStr, timeStr, ampm);
    if (v) return v;
    if (dateStr) return `${dateStr}${timeStr ? ` ${timeStr} ${ampm}` : ''}`;
    return "";
  };

  const propertyPulldown = () => {
    return propertyList?.length > 0 ? (
      <select
        key={propertyList}
        value={propertyPrefix}
        name="propertyPrefix"
        className="custom-select backoffice-pulldown-list"
        onChange={(e) => setPropertyPrefix(e.target.value)}
      >
        <option value="-1" disabled>
          Select the property name
        </option>
        {propertyList.map((propertyEntry) => (
          <option key={propertyEntry.propertyId} value={propertyEntry.propertyPrefix}>
            {propertyEntry.propertyName}
          </option>
        ))}
      </select>
    ) : (
      <div className="alert alert-danger" role="alert">
        <p>Property list not loaded from server. Please refresh and try again.</p>
      </div>
    );
  };

  const onSubmit = (e) => {
    e.preventDefault();

    // Validate required fields
    const missing = [];
    if (!propertyPrefix || propertyPrefix === "") missing.push("Property Prefix");
    if (!swipeDate || swipeDate.trim() === "") missing.push("Swipe Date");
    if (!swipeTime || swipeTime.trim() === "") missing.push("Swipe Time");
    if (!ticketId || ticketId.trim() === "") missing.push("Ticket ID");
    if (!firstName || firstName.trim() === "") missing.push("First Name");
    if (!lastName || lastName.trim() === "") missing.push("Last Name");
    if (!playerId || playerId.trim() === "") missing.push("Player ID");
    if (!dob || dob.trim() === "") missing.push("Date of Birth");
    if (!brand || brand.trim() === "") missing.push("Brand");
    if (!amount || amount.trim() === "") missing.push("Amount");
    if (!expirationDate || expirationDate.trim() === "") missing.push("Expiration Date");
    if (!expirationTime || expirationTime.trim() === "") missing.push("Expiration Time");

    if (missing.length > 0) {
      const msg = "The following fields are required: " + missing.join(", ");
      setErrorMessage(msg);
      toast.error(msg);
      return;
    }

    setErrorMessage("");
    setShowConfirm(true);
  };

  const confirmSubmit = async () => {
    const swipeDateTime = buildDateTime(swipeDate, swipeTime, swipeAmPm);
    const expireDateTime = buildDateTime(expirationDate, expirationTime, expirationAmPm);

    const payload = {
      propertyPrefix,
      swipeDateTime,
      ticketId,
      firstName,
      lastName,
      playerId,
      dob,
      zipCode,
      brand,
      amount,
      expireDateTime
    };

    try {
      await dispatch(addOffer(payload));
      setShowConfirm(false);
      resetForm();
    } catch (err) {
      setShowConfirm(false);
    }
  };

  return (
    <div className="admin-screen-divider-div add-offer-compact">

      <h3>Add Offer</h3>
      {errorMessage && (
        <div className="backoffice-error-messages">
          <div className="alert alert-danger" role="alert">
            <p>{errorMessage}</p>
          </div>
        </div>
      )}

      <Container fluid>
        <Form onSubmit={onSubmit} className="gcotg-form">
          <Row>
            <Col md={4}>
              <Form.Group className="gcotg-form-group">
                <Form.Label className="backoffice-form-label">Property Prefix</Form.Label>
                {propertyPulldown()}
              </Form.Group>

              <Form.Group className="gcotg-form-group">
                <Form.Label className="backoffice-form-label">Swipe Date (MM/DD/YYYY)</Form.Label>
                <Form.Control
                  as={InputMask}
                  mask="99/99/9999"
                  placeholder="mm/dd/yyyy"
                  value={swipeDate}
                  onChange={(e) => setSwipeDate(e.target.value)}
                  className="form-control gcotg-form-element"
                />
              </Form.Group>

              <Form.Group className="gcotg-form-group">
                <Form.Label className="backoffice-form-label">Swipe Time (HH:MM)</Form.Label>
                <div className="d-flex align-items-center">
                  <Form.Control
                    as={InputMask}
                    mask="99:99"
                    placeholder="hh:mm"
                    value={swipeTime}
                    onChange={(e) => setSwipeTime(e.target.value)}
                    className="form-control gcotg-form-element time-input"
                  />
                  <Form.Check inline label="AM" type="radio" id="swipe-am" name="swipeAmPm" checked={swipeAmPm === "AM"} onChange={() => setSwipeAmPm("AM")} />
                  <Form.Check inline label="PM" type="radio" id="swipe-pm" name="swipeAmPm" checked={swipeAmPm === "PM"} onChange={() => setSwipeAmPm("PM")} />
                </div>
              </Form.Group>

              <Form.Group className="gcotg-form-group">
                <Form.Label className="backoffice-form-label">Ticket ID</Form.Label>
                <Form.Control value={ticketId} onChange={(e) => setTicketId(e.target.value)} className="form-control gcotg-form-element" />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="gcotg-form-group">
                <Form.Label className="backoffice-form-label">First Name</Form.Label>
                <Form.Control value={firstName} onChange={(e) => setFirstName(e.target.value)} className="form-control gcotg-form-element" />
              </Form.Group>

              <Form.Group className="gcotg-form-group">
                <Form.Label className="backoffice-form-label">Last Name</Form.Label>
                <Form.Control value={lastName} onChange={(e) => setLastName(e.target.value)} className="form-control gcotg-form-element" />
              </Form.Group>

              <Form.Group className="gcotg-form-group">
                <Form.Label className="backoffice-form-label">Player ID</Form.Label>
                <Form.Control value={playerId} onChange={(e) => setPlayerId(e.target.value)} className="form-control gcotg-form-element" />
              </Form.Group>

              <Form.Group className="gcotg-form-group">
                <Form.Label className="backoffice-form-label">Date of Birth (MM/DD/YYYY)</Form.Label>
                <Form.Control
                  as={InputMask}
                  mask="99/99/9999"
                  placeholder="mm/dd/yyyy"
                  value={dob}
                  onChange={(e) => setDob(e.target.value)}
                  className="form-control gcotg-form-element"
                />
              </Form.Group>
              <Form.Group className="gcotg-form-group">
                <Form.Label className="backoffice-form-label">ZIP Code</Form.Label>
                <Form.Control value={zipCode} onChange={(e) => setZipCode(e.target.value)} className="form-control gcotg-form-element" />
              </Form.Group>
            </Col>

            <Col md={4}>
              {/* ZIP Code moved to column 2 to reduce vertical space */}

              <Form.Group className="gcotg-form-group">
                <Form.Label className="backoffice-form-label">Brand</Form.Label>
                <Form.Control value={brand} onChange={(e) => setBrand(e.target.value)} className="form-control gcotg-form-element" />
              </Form.Group>

              <Form.Group className="gcotg-form-group">
                <Form.Label className="backoffice-form-label">Amount</Form.Label>
                <Form.Control value={amount} onChange={(e) => setAmount(e.target.value)} className="form-control gcotg-form-element" />
              </Form.Group>

              <Form.Group className="gcotg-form-group">
                <Form.Label className="backoffice-form-label">Expiration Date (MM/DD/YYYY)</Form.Label>
                <Form.Control
                  as={InputMask}
                  mask="99/99/9999"
                  placeholder="mm/dd/yyyy"
                  value={expirationDate}
                  onChange={(e) => setExpirationDate(e.target.value)}
                  className="form-control gcotg-form-element"
                />
              </Form.Group>

              <Form.Group className="gcotg-form-group">
                <Form.Label className="backoffice-form-label">Expiration Time (HH:MM)</Form.Label>
                <div className="d-flex align-items-center">
                  <Form.Control
                    as={InputMask}
                    mask="99:99"
                    placeholder="hh:mm"
                    value={expirationTime}
                    onChange={(e) => setExpirationTime(e.target.value)}
                    className="form-control gcotg-form-element time-input"
                  />
                  <Form.Check inline label="AM" type="radio" id="exp-am" name="expAmPm" checked={expirationAmPm === "AM"} onChange={() => setExpirationAmPm("AM")} />
                  <Form.Check inline label="PM" type="radio" id="exp-pm" name="expAmPm" checked={expirationAmPm === "PM"} onChange={() => setExpirationAmPm("PM")} />
                </div>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col className="text-right">
              <Button variant="secondary" className="gcotg-button-secondary mr-2" onClick={resetForm}>
                Reset
              </Button>
              <Button variant="primary" className="gcotg-button" type="submit">
                Submit
              </Button>
            </Col>
          </Row>
        </Form>
      </Container>

      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} className="add-offer-confirm-modal">
        <Modal.Header closeButton>
          <Modal.Title>Confirm Add Offer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Please confirm the values below before submitting:</p>
          <dl>
            <dt>Property Prefix</dt>
            {/* propertyId may be a string from the select; property.propertyId may be a number
                so compare as strings to reliably find the matching propertyName */}
            <dd>{propertyPrefix}</dd>
            <dt>Swipe Date/Time</dt>
            <dd>{formatSwipeDisplay(swipeDate, swipeTime, swipeAmPm)}</dd>
            <dt>Ticket ID</dt>
            <dd>{ticketId}</dd>
            <dt>First Name</dt>
            <dd>{firstName}</dd>
            <dt>Last Name</dt>
            <dd>{lastName}</dd>
            <dt>Player ID</dt>
            <dd>{playerId}</dd>
            <dt>Date of Birth</dt>
            <dd>{dob}</dd>
            <dt>ZIP Code</dt>
            <dd>{zipCode}</dd>
            <dt>Brand</dt>
            <dd>{brand}</dd>
            <dt>Amount</dt>
            <dd>{amount}</dd>
            <dt>Expiration</dt>
            <dd>{formatSwipeDisplay(expirationDate, expirationTime, expirationAmPm)}</dd>
          </dl>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" className="gcotg-button-secondary" onClick={() => setShowConfirm(false)}>
            Cancel
          </Button>
          <Button variant="primary" className="gcotg-button" onClick={confirmSubmit}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AddOfferForm;



